import {Component, Input, OnChanges, SimpleChanges} from '@angular/core';
import {CmEdCoverageCardView} from "../../interface/cm-ed-coverage-card-view.interface";
import {CmEdSize} from "../../interface/cm-ed-size.type";
import {CmContractExternalDataFoldPt} from "../../interface/cm-contract-external-data-fold-pt.interface";
import {
    ContractExternalDataCrossLinkDto
} from "../../../../../../../../ci/model/dto/api/contract-external-data-cross-link.dto";
import {CmEdCrossLinkPt} from "../../interface/cm-ed-cross-link-pt.interface";
import {
    buildCrossLinkPt,
    crossLinkSeverity as buildCrossLinkSeverity
} from "../../util/cm-ed-cross-link-pt.util";
import {cmEdHueTokens, CmEdHueTokens, cmEdSparteHue} from "../cm-ed-hue.util";
import {cmEdCountHits, cmEdHasHit} from "../cm-ed-search-hit.util";

/**
 * ONE Deckung as a card — the unit of the external-data overview.
 *
 * A Deckung is what a clerk actually looks for ("was ist bei Tim versichert?"), so it is the thing
 * that gets a card: heading, the figures that matter, the person or object it is linked to, and one
 * expander for the rest. The same card is used by V1, V2 and V3; the views differ in how many of
 * them fit next to each other, never in what a card shows.
 *
 * <h2>Two details the layout stands or falls on</h2>
 * The heading ELLIPSISES and never wraps — "Rechtsschutz — Verkehr" in a one-column card is the
 * test case, and a wrapped heading pushes the figures of every neighbouring card out of alignment.
 * A flex child does not ellipsise without `min-w-0` (its default `min-width: auto` refuses to shrink
 * below its content), which is the single most common way to get this wrong, so the class sits on
 * the heading in the template and is not to be removed.
 *
 * <h2>What it deliberately does NOT do</h2>
 * <ul>
 *   <li>It does not load anything and holds no service. Everything it renders arrives as one input.</li>
 *   <li>It does not FORMAT: every value is printed as the server sent it.</li>
 *   <li>It does not render level 3. It STATES how many such fields there are, beside the expander, and
 *       leaves it at that: "Alle Daten" is the one place that shows everything — an overview that can
 *       be expanded into the full sheet is not an overview any more — but a reader who expands the
 *       card and still misses a field must be told the field exists, not left to conclude it was
 *       never delivered.</li>
 *   <li>It does not NAVIGATE on a cross-link. The chips state the relation the delivery carries;
 *       where a click on one should lead is a decision of the view that owns the sheet.</li>
 *   <li>It does not persist its expander. A reader returning to the sheet finds the card in its
 *       resting state, never one that silently hides half its data.</li>
 *   <li>It does not MATCH and it does not HIGHLIGHT a pair. The search runs once in the host; the
 *       card only reveals what a term found and states how much of it there is — see
 *       {@link searching}.</li>
 * </ul>
 */
@Component({
    selector: 'mvs-cm-ed-coverage-card',
    templateUrl: './cm-ed-coverage-card.component.html',
    styleUrls: ['./cm-ed-coverage-card.component.scss'],
    standalone: false,
})
export class CmEdCoverageCardComponent implements OnChanges {

    /** Counts every instance ever created, so two cards never share a DOM id. */
    protected static instanceCounter: number = 0;

    /** The Deckung to render. */
    @Input() card: CmEdCoverageCardView;

    /** Width bucket, measured once by the host. Decides the type scale and hides the sub line at `xs`. */
    @Input() size: CmEdSize = 'lg';

    /**
     * Whether a search term is currently active — passed through to the pairs, and the switch for the
     * two things the card itself does about a search.
     *
     * <h2>The card NEVER hides and NEVER dims</h2>
     * A card with no hit at all looks completely ordinary while a term is active: same border, same
     * tint, same figures, full opacity. Nothing on this sheet is faded or removed by a search — the
     * user rejected that outright — so a search can only ever ADD a highlight, never subtract
     * attention from what did not match.
     *
     * <h2>Which makes the two things it DOES do carry the whole load</h2>
     * <ul>
     *   <li>It STATES its hits ({@link CmEdCoverageCardView#hitCount}). Since nothing dims, a card
     *       holding three matches inside its collapsed level-2 block is visually indistinguishable
     *       from one holding none, and the count is the only thing on it that says otherwise.</li>
     *   <li>It OPENS itself when the match is behind its expander — see {@link open}. A match the
     *       reader is never shown is worse than no match at all on a screen whose promise is that
     *       nothing is hidden.</li>
     * </ul>
     *
     * A boolean and not the term: the card has no use for the text, and being handed it is what would
     * tempt the next author into matching locally. Defaults to `false`, i.e. to exactly the card these
     * three views have rendered all along.
     */
    @Input() searching: boolean = false;

    /**
     * THE READER'S OWN expander state — what the card returns to when the term is cleared.
     *
     * Component-local and deliberately not persisted across a visit. It is deliberately NOT touched by
     * the automatic reveal either: a card force-opened by a search must fall shut again exactly as the
     * reader left it, and writing the automatic state into this field is how "returns to the reader's
     * own state" quietly becomes "stays open forever".
     */
    expanded: boolean = false;

    /**
     * The reader's decision DURING an active search — `null` while they have not made one.
     *
     * READER INTENT WINS: a card that opened itself because the term was found in its Weitere Angaben
     * can still be closed, and one the term left shut can still be opened. Without this the expander
     * would be a dead control for exactly the cards a reader is most likely to poke at.
     *
     * Dropped whenever the search state or the card changes ({@link ngOnChanges}), because a new term
     * is a new question and the answer to the old one must not survive it. Never persisted.
     */
    protected searchOverride: boolean | null = null;

    /**
     * DOM id of the level-2 block, so the expander can point `aria-controls` at what it reveals.
     * Instance-scoped, because a grid renders a dozen of these cards at once.
     */
    protected readonly level2Id: string;

    /** The `aria-expanded` value {@link foldPtCache} currently states; `null` before the first read. */
    private foldPtOpen: boolean | null = null;

    /** The cached pass-through — see {@link foldPt} for why it is cached rather than rebuilt. */
    private foldPtCache: CmContractExternalDataFoldPt | null = null;

    constructor() {
        this.level2Id = 'cmEdCoverage' + (++CmEdCoverageCardComponent.instanceCounter) + '-more';
    }

    /**
     * The hue of this card's SPARTE, as its utility classes — the first job colour does on this sheet.
     *
     * Picked by {@link CmEdCoverageCardView#accentIndex} and therefore stable per Sparte: the three
     * Deckungen of one Sparte wear the same spine AND the same header tint, which is what lets a
     * reader group a flat card grid back into Sparten at a glance, and what makes Hausrat and
     * Rechtsschutz separate without reading a word.
     *
     * The mapping, the cycle and the reason `orange` is in it live in `cm-ed-hue.util.ts` — the ONE
     * place, shared with {@link CmEdBlockSectionComponent}, because a Sparte section and the cards
     * built from that Sparte must wear one hue or they stop reading as one family. Two copies of the
     * palette with a comment saying they must match is what this replaced.
     *
     * The accent is IDENTITY, not status: a green card on the third Sparte says "third Sparte", never
     * "in Ordnung". That is exactly why the hues are spent in a fixed, documented order and never
     * derived from a value — the sheet has no status semantics to derive one from.
     *
     * Defaults to index 0 for a card with no index at all, which repeats the first accent rather than
     * leaving the spine blank; a blank spine reads as a rendering fault.
     */
    get hueTokens(): CmEdHueTokens {
        return cmEdHueTokens(cmEdSparteHue(this.card?.accentIndex ?? 0));
    }

    /** The accent utility class of this card's spine. */
    get accentClass(): string {
        return this.hueTokens.spine;
    }

    /**
     * Background of the header BAND — the 50 step of the card's own Sparte hue.
     *
     * It was `surface-100`, the sheet's neutral band, and that grey is precisely what made a grid of
     * a dozen cards read as one undifferentiated wall: the spine is 0.25 rem wide and is the only
     * thing that ever told the three Unfall cards from the two Hausrat cards. The tint spends the
     * SAME hue over the whole band, so the grouping is visible from across the room while the title
     * on top of it keeps `text-color` and full contrast.
     *
     * The 50 step and not the 100: a header band is a title strip, not a panel, and it sits directly
     * against `surface-card`. Person and object cards keep `surface-100` — they belong to no Sparte,
     * and a tint there would state a grouping that does not exist.
     */
    get tintClass(): string {
        return this.hueTokens.tint;
    }

    /**
     * A card sized to its bucket: tighter padding where the grid puts a card into one column.
     *
     * All six steps written out, the widest included. From `md` up the grid holds a card at
     * ~310-380 px WHATEVER the host width — six columns of 2111 px is ~330 px, two of 760 px is
     * ~370 px — so the padding has no reason to change along the top of the ladder, and stating that
     * is not the same as letting a `default` decide it.
     */
    get padClass(): string {

        switch (this.size) {
            case 'xxl':
            case 'xl':
            case 'lg':
            case 'md':
            case 'sm':
                return 'p-3';
            case 'xs':
                // Below 560 px the card spans the host; 1rem on each side is width the figures need
                // more than the frame does.
                return 'p-2';
        }
    }

    /**
     * Padding of the header BAND — tighter than the body, so the band reads as a title strip rather
     * than as a second panel.
     *
     * Its horizontal step matches {@link padClass} exactly, which is what puts the title on the same
     * left edge as the figures underneath it; only the vertical step is smaller. All six buckets are
     * answered through the same switch shape the rest of this component uses, so a step added to the
     * ladder cannot be answered for the body and missed for the band.
     */
    get headClass(): string {

        switch (this.size) {
            case 'xxl':
            case 'xl':
            case 'lg':
            case 'md':
            case 'sm':
                return 'px-3 py-2';
            case 'xs':
                return 'px-2 py-1';
        }
    }

    /**
     * The severity of ONE cross-link chip — `info` for a person, `secondary` for an object.
     *
     * Delegates to {@link crossLinkSeverity}, for the same reason {@link crossLinkPt} delegates to
     * {@link buildCrossLinkPt}: the "Alle Daten" view draws its own row sub-headings and therefore
     * renders the chips itself, so the colour and the accessible name of a kind must both have ONE
     * definition or a person chip ends up blue here and grey there.
     */
    crossLinkSeverity(link: ContractExternalDataCrossLinkDto): 'info' | 'secondary' {
        return buildCrossLinkSeverity(link);
    }

    /**
     * Type scale of the card heading.
     *
     * `text-base` from `sm` up, i.e. from 560 px. The old rule read `xs || sm` and meant "below
     * 600 px"; `sm` now reaches 759 px, where a card is col-12 and therefore the WIDEST card on the
     * whole ladder — shrinking its heading there would have been the ladder's own rename working
     * against the width it was re-cut to use.
     */
    get titleClass(): string {

        switch (this.size) {
            case 'xxl':
            case 'xl':
            case 'lg':
            case 'md':
            case 'sm':
                return 'text-base';
            case 'xs':
                return 'text-sm';
        }
    }

    /**
     * The accessible name of ONE cross-link chip, as a PrimeNG pass-through onto the p-tag element.
     *
     * Delegates to {@link buildCrossLinkPt} and adds nothing: "Alle Daten" draws its own row
     * sub-headings and renders the same chips without going through this card, so the wording and the
     * role are defined once in the util and merely projected into the template here. The reasoning
     * behind both lives on {@link CmEdCrossLinkPt}.
     */
    crossLinkPt(link: ContractExternalDataCrossLinkDto): CmEdCrossLinkPt {
        return buildCrossLinkPt(link);
    }

    /**
     * True while a term is active AND this card's Weitere Angaben hold one of its matches.
     *
     * Reads the flag the HOST set on each field and aggregates it — it does not fold a string, see no
     * term and compare nothing (`cm-ed-search-hit.util.ts`). The sheet has one matcher; a second one
     * here is how the header's count and the sheet's highlights would come to disagree.
     */
    get level2Hit(): boolean {
        return this.searching && cmEdHasHit(this.card?.level2);
    }

    /**
     * True when the term matched one of the card's OWN TEXTS rather than one of its pairs — the switch
     * for the mark on the heading.
     *
     * <h2>The gap it closes</h2>
     * A card TITLE is part of the host's haystack and is counted into
     * {@link CmEdCoverageCardView#hitCount}, and nothing on the card showed it: the "{n} Treffer"
     * marker went up and no text on the card changed, so a reader could see THAT the card matched and
     * never WHERE. On a screen whose promise is that nothing is hidden, a count that points at nothing
     * is the one failure mode that undoes the promise.
     *
     * <h2>By subtraction, because there is no matcher here and never will be</h2>
     * The sheet has ONE matcher, in the host. This reads the flags the host wrote: the card's total
     * minus the hits its rendered pairs account for. What is left is the surplus the card's own texts
     * carry — exact for a card matched on its title AND on two of its figures, and zero for one matched
     * on figures alone, because the host builds the total by addition from the same parts.
     *
     * <h2>What the surplus can be, stated plainly</h2>
     * `hitCount` folds in the title, the sub AND the cross-link labels, so a Deckung found only through
     * "↔ Anna Meier" also lands here and marks the heading rather than the chip that carried the term.
     * The heading is where the reader looks first and the chips are two lines below it, so the mark is
     * still pointing into the right card — but it is a heading-level statement, not a
     * character-level one, and the component says so rather than pretending otherwise. Marking exactly
     * the run that matched needs highlight SEGMENTS for the title, the sub and each cross-link label,
     * which is a host change: this component has no term and must never grow one.
     */
    get ownTextHit(): boolean {

        if (!this.searching || !this.card) {
            return false;
        }

        const pairs = cmEdCountHits(this.card.level1) + cmEdCountHits(this.card.level2);

        return this.card.hitCount > pairs;
    }

    /**
     * WHETHER THE LEVEL-2 BLOCK IS ON SCREEN — the reader's own state, temporarily overruled by a
     * search that found something behind the expander.
     *
     * <pre>
     *   no term          the reader's own {@link expanded}, untouched
     *   term, no override   open if the reader had it open OR the term matched inside level 2
     *   term, override      the reader's decision, which always wins
     * </pre>
     *
     * The reveal is an OVERLAY and never a write: {@link expanded} is not modified, so clearing the
     * term returns every card to precisely the state its reader left it in. That is the same contract
     * the sheet's value-clamp reveal ran under — the automatic disclosure is temporary, reader intent
     * beats it, and neither is ever persisted.
     *
     * A getter and not a field kept in sync: it reads three inputs and a flag the host may recompute
     * on the very same card object while the reader types, and a cached copy would then state the
     * opposite of what is rendered.
     */
    get open(): boolean {

        if (!this.searching) {
            return this.expanded;
        }

        return this.searchOverride ?? (this.expanded || this.level2Hit);
    }

    /**
     * The expander's `aria-expanded` / `aria-controls`; see {@link CmContractExternalDataFoldPt}.
     *
     * CACHED ON THE VALUE IT STATES, not rebuilt on toggle. `[pt]` is read by PrimeNG when the
     * binding's IDENTITY changes, so the object must be new exactly when {@link open} changes and the
     * same object at every other moment. Rebuilding it in `toggle()` covered only one of the three
     * ways `open` now moves — the reader's click, the search flipping on or off, and the host
     * recomputing hits on the same card object while the term grows — and the other two would have
     * left a screen reader told the opposite of what is on screen.
     */
    protected get foldPt(): CmContractExternalDataFoldPt {

        const open = this.open;

        if (this.foldPtOpen !== open || !this.foldPtCache) {
            this.foldPtOpen = open;
            this.foldPtCache = {
                root: {
                    'aria-expanded': String(open),
                    'aria-controls': this.level2Id,
                },
            };
        }

        return this.foldPtCache;
    }

    /**
     * Drops the reader's search-time override whenever the question changes.
     *
     * A new term, a search switched on or off, or a new card is a new question: the answer to the old
     * one must not survive it, or a card closed against one term would stay shut against the next.
     * {@link expanded} — the reader's own, search-free state — is deliberately left alone.
     */
    ngOnChanges(changes: SimpleChanges): void {

        if (changes["searching"] || changes["card"]) {
            this.searchOverride = null;
        }
    }

    /**
     * Opens / closes the level-2 block.
     *
     * While a term is active the click is recorded as an OVERRIDE rather than as the reader's resting
     * state: a card the search opened must be closable, and a card closed while searching must not
     * still be closed once the term is cleared. Without the split, `expanded = !expanded` on a
     * force-opened card would flip a flag that was not what put the block on screen, and the control
     * would appear to do nothing.
     */
    toggle(): void {

        if (this.searching) {
            this.searchOverride = !this.open;
            return;
        }

        this.expanded = !this.expanded;
    }
}
